<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import dealReportsAPI from 'dashboard/api/dealReports';
import { useMapGetter } from 'dashboard/composables/store';
import { useCommercialGoalsStore } from 'dashboard/stores/commercialGoals';
import { useCommercialGoalFormat } from 'dashboard/components-next/CommercialGoals/useCommercialGoalFormat';
import { goalPace } from 'dashboard/components-next/CommercialGoals/commercialGoalPace';
import GoalRing from 'dashboard/components-next/CommercialGoals/GoalRing.vue';
import GoalProgressBar from 'dashboard/components-next/CommercialGoals/GoalProgressBar.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ActivityFeed from './ActivityFeed.vue';
import { findUserGoals } from './commercialGoalMatchers';
import { formatCurrencyBRL } from './commercialFormatters';

const props = defineProps({
  params: { type: Object, required: true },
});

const RANKING_SIZE = 5;

const PACE_PILL = {
  achieved: 'bg-n-teal-3 text-n-teal-11',
  ahead: 'bg-n-teal-3 text-n-teal-11',
  on_track: 'bg-n-amber-3 text-n-amber-11',
  at_risk: 'bg-n-ruby-3 text-n-ruby-11',
  in_progress: 'bg-n-alpha-2 text-n-slate-11',
  no_data: 'bg-n-alpha-2 text-n-slate-11',
};

const { t } = useI18n();
const { formatValue, formatType, formatPeriod } = useCommercialGoalFormat();
const goalsStore = useCommercialGoalsStore();
const agents = useMapGetter('agents/getAgents');

const teamGoals = computed(() =>
  goalsStore.progress.filter(goal => goal.scopeType === 'team')
);

const paceFor = goal => goalPace(goal);

const dailyNeeded = (goal, pace) => {
  if (pace.dailyNeeded === null || pace.dailyNeeded <= 0) return '';
  const value =
    goal.goalType === 'sales_count'
      ? Math.ceil(pace.dailyNeeded)
      : pace.dailyNeeded;
  return t('DASHBOARD.COMERCIAL.GOALS_HERO.PACE_DETAIL', {
    days: pace.daysLeft,
    value: formatValue(goal.goalType, value),
  });
};

const rows = ref([]);

const fetchRanking = async () => {
  try {
    const response = await dealReportsAPI.getSalesReps(props.params);
    rows.value = response.data;
  } catch {
    rows.value = [];
  }
};

watch(() => props.params, fetchRanking, { immediate: true, deep: true });

const avatarFor = userId =>
  agents.value.find(agent => agent.id === userId)?.thumbnail || '';

// Campeão e ranking: vendedores com venda no período (quem não vendeu não entra no pódio).
const ranking = computed(() =>
  rows.value
    .filter(row => row.user_id !== null && row.won_count > 0)
    .sort((a, b) => b.won_count - a.won_count || b.revenue - a.revenue)
    .map(row => ({
      ...row,
      goals: findUserGoals(goalsStore.progress, row.user_id),
    }))
);

const champion = computed(() => ranking.value[0] || null);
const runnersUp = computed(() => ranking.value.slice(1, RANKING_SIZE));
</script>

<template>
  <section class="grid gap-4 lg:grid-cols-3">
    <div class="flex flex-col gap-4 p-4 border rounded-lg border-n-weak">
      <h3 class="m-0 font-medium text-n-slate-12">
        {{ t('DASHBOARD.COMERCIAL.GOALS_HERO.TEAM_TITLE') }}
      </h3>
      <p v-if="!teamGoals.length" class="text-sm text-n-slate-10">
        {{ t('DASHBOARD.COMERCIAL.GOALS_HERO.EMPTY') }}
      </p>
      <div
        v-for="goal in teamGoals"
        :key="goal.id"
        class="flex items-center gap-4"
      >
        <GoalRing
          :percent="goal.progress.progressPercent"
          :state="goal.progress.state"
        />
        <div class="flex flex-col min-w-0 gap-1">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ formatType(goal) }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              t('DASHBOARD.COMERCIAL.GOALS_HERO.OF', {
                current: formatValue(
                  goal.goalType,
                  goal.progress.currentValue ?? 0
                ),
                target: formatValue(goal.goalType, goal.progress.targetValue),
              })
            }}
          </span>
          <span class="text-xs text-n-slate-10">{{ formatPeriod(goal) }}</span>
          <span
            class="px-2 py-0.5 text-xs font-medium rounded-full w-fit"
            :class="PACE_PILL[paceFor(goal).status]"
          >
            {{
              t(
                `DASHBOARD.COMERCIAL.GOALS_HERO.PACE.${paceFor(goal).status.toUpperCase()}`
              )
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ dailyNeeded(goal, paceFor(goal)) }}
          </span>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak">
      <h3 class="m-0 font-medium text-n-slate-12">
        {{ t('DASHBOARD.COMERCIAL.GOALS_HERO.RANKING_TITLE') }}
      </h3>
      <p v-if="!champion" class="text-sm text-n-slate-10">
        {{ t('DASHBOARD.COMERCIAL.GOALS_HERO.RANKING_EMPTY') }}
      </p>
      <template v-else>
        <div
          class="flex flex-col items-center gap-1 p-4 rounded-lg bg-n-amber-2"
        >
          <Icon icon="i-lucide-crown" class="size-6 text-n-amber-10" />
          <Avatar
            :src="avatarFor(champion.user_id)"
            :name="champion.user_name"
            :size="64"
            rounded-full
          />
          <span class="text-lg font-semibold text-n-slate-12">
            {{ champion.user_name }}
          </span>
          <span
            class="px-3 py-0.5 text-sm font-medium rounded-full bg-n-amber-4 text-n-amber-11"
          >
            {{
              t(
                'DASHBOARD.COMERCIAL.GOALS_HERO.SALES',
                { n: champion.won_count },
                champion.won_count
              )
            }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ formatCurrencyBRL(champion.revenue) }}
          </span>
          <div
            v-for="goal in champion.goals"
            :key="goal.id"
            class="w-full max-w-48"
          >
            <GoalProgressBar
              compact
              :percent="goal.progress.progressPercent"
              :state="goal.progress.state"
            />
          </div>
        </div>
        <ul class="flex flex-col gap-2 m-0">
          <li
            v-for="(row, index) in runnersUp"
            :key="row.user_id"
            class="flex items-center gap-3"
          >
            <span class="w-6 text-sm text-n-slate-10">
              {{
                t('DASHBOARD.COMERCIAL.GOALS_HERO.POSITION', { n: index + 2 })
              }}
            </span>
            <Avatar
              :src="avatarFor(row.user_id)"
              :name="row.user_name"
              :size="28"
              rounded-full
            />
            <span class="flex-1 text-sm truncate text-n-slate-12">
              {{ row.user_name }}
            </span>
            <div v-if="row.goals.length" class="w-20">
              <GoalProgressBar
                compact
                :percent="row.goals[0].progress.progressPercent"
                :state="row.goals[0].progress.state"
              />
            </div>
            <span class="text-sm font-medium text-n-slate-12">
              {{ row.won_count }}
            </span>
          </li>
        </ul>
      </template>
    </div>

    <ActivityFeed :params="params" />
  </section>
</template>
